import React, { useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";

const BENEFIT_KEYS = [
  "benefit1",
  "benefit2",
  "benefit3",
  "benefit4",
  "benefit5",
  "benefit6",
];

const ICONS = [
  "fas fa-bullhorn",
  "fas fa-users",
  "fas fa-award",
  "fas fa-camera",
  "fas fa-handshake",
  "fas fa-puzzle-piece",
];

function buildBenefits(t) {
  return BENEFIT_KEYS.map((key, index) => {
    const baseKey = `sponsoringBenefits.${key}`;
    const rawDetails = t(`${baseKey}.details`, { returnObjects: true });
    const details = Array.isArray(rawDetails)
      ? rawDetails
      : typeof rawDetails === "object" && rawDetails !== null
        ? Object.values(rawDetails)
        : [];

    return {
      id: key,
      title: t(`${baseKey}.title`),
      description: t(`${baseKey}.description`),
      icon: ICONS[index],
      details: details.map((d) => ({
        subtitle: d?.subtitle ?? "",
        text: d?.text ?? "",
      })),
    };
  });
}

function SponsoringBenefits() {
  const { t, i18n } = useTranslation();
  const base = process.env.PUBLIC_URL || "";
  const [activeIndex, setActiveIndex] = useState(null);

  const items = useMemo(() => buildBenefits(t), [t, i18n.language]);

  const toggleAccordion = useCallback((index) => {
    setActiveIndex((prev) => (prev === index ? null : index));
  }, []);

  const eventMapHref = `${base}/documents/Event-Mappe.pdf`;
  const eventMapFileName = "GCWAA-Event-Mappe.pdf";

  return (
    <section
      className="relative z-10 bg-slate-50/50 pb-14 pt-24 transition-colors dark:bg-slate-900 md:pb-16 md:pt-24"
      id="sponsoring-benefits"
      aria-labelledby="sponsoring-benefits-heading"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <header className="mb-10 text-center md:mb-12">
          <h2
            id="sponsoring-benefits-heading"
            className="relative mb-5 inline-block text-4xl font-semibold tracking-tight text-primary dark:text-primary-light md:text-5xl"
          >
            {t("sponsoringBenefits.title")}
            <span
              className="absolute -bottom-2.5 left-1/2 h-0.5 w-16 -translate-x-1/2 rounded-full bg-primary dark:bg-primary-light"
              aria-hidden
            />
          </h2>
          <p className="mx-auto mt-5 max-w-3xl text-pretty text-lg leading-relaxed text-slate-600 dark:text-slate-400">
            {t("sponsoringBenefits.description")}
          </p>
        </header>

        <ul
          className="m-0 mb-10 grid list-none grid-cols-1 gap-5 p-0 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3"
          aria-label={t("sponsoringBenefits.list_label")}
        >
          {items.map((benefit, index) => {
            const isActive = activeIndex === index;
            const panelId = `sponsoring-benefit-panel-${benefit.id}`;
            const triggerId = `sponsoring-benefit-trigger-${benefit.id}`;

            return (
              <li key={benefit.id} className="min-h-0">
                <article
                  className={`flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all dark:border-slate-700 dark:bg-slate-800 motion-safe:hover:shadow-md ${
                    isActive
                      ? "border-l-4 border-l-primary ring-1 ring-primary/15 dark:border-l-primary-light dark:ring-primary-light/20"
                      : "border-l-4 border-l-slate-300 dark:border-l-slate-600"
                  }`}
                >
                  <button
                    type="button"
                    id={triggerId}
                    className="flex w-full flex-col gap-4 p-5 text-left transition-colors hover:bg-slate-50/80 dark:hover:bg-slate-700/30 sm:p-6 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/35 dark:focus-visible:ring-primary-light/35"
                    aria-expanded={isActive}
                    aria-controls={panelId}
                    onClick={() => toggleAccordion(index)}
                    aria-label={
                      isActive
                        ? t("sponsoringBenefits.collapse_details_aria", {
                            title: benefit.title,
                          })
                        : t("sponsoringBenefits.expand_details_aria", {
                            title: benefit.title,
                          })
                    }
                  >
                    <div className="flex w-full items-start gap-4">
                      <div
                        className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl transition-colors sm:h-14 sm:w-14 ${
                          isActive
                            ? "bg-primary text-white dark:bg-primary-light dark:text-slate-900"
                            : "bg-primary/10 dark:bg-primary-light/15"
                        }`}
                        aria-hidden
                      >
                        <i
                          className={`${benefit.icon} text-lg sm:text-xl ${
                            isActive
                              ? "text-white dark:text-slate-900"
                              : "text-primary dark:text-primary-light"
                          }`}
                        />
                      </div>
                      <div className="min-w-0 flex-1 pr-1">
                        <h3 className="text-base font-semibold leading-snug text-slate-900 dark:text-slate-100 sm:text-lg">
                          {benefit.title}
                        </h3>
                        <p className="mt-1.5 text-pretty text-sm leading-relaxed text-slate-600 dark:text-slate-400">
                          {benefit.description}
                        </p>
                      </div>
                      <span
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400"
                        aria-hidden
                      >
                        <i
                          className={`fas fa-chevron-${isActive ? "up" : "down"} text-xs`}
                        />
                      </span>
                    </div>
                  </button>

                  <div
                    id={panelId}
                    role="region"
                    aria-labelledby={triggerId}
                    hidden={!isActive}
                    className={
                      isActive
                        ? "border-t border-slate-200 px-5 pb-5 pt-4 dark:border-slate-700 sm:px-6 sm:pb-6"
                        : "hidden"
                    }
                  >
                    {isActive ? (
                      <ul className="m-0 list-none space-y-4 p-0">
                        {benefit.details.map((detail, i) => (
                          <li
                            key={`${benefit.id}-d-${i}`}
                            className="border-l-2 border-slate-200 pl-3 dark:border-slate-600 sm:pl-4"
                          >
                            <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                              {detail.subtitle}
                            </h4>
                            <p className="mt-1 text-pretty text-sm leading-relaxed text-slate-600 dark:text-slate-400">
                              {detail.text}
                            </p>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                </article>
              </li>
            );
          })}
        </ul>

        <div className="mx-auto max-w-xl">
          <a
            href={eventMapHref}
            download={eventMapFileName}
            className="group flex items-center gap-4 rounded-2xl border border-slate-200 border-l-4 border-l-primary bg-white p-4 shadow-sm transition-all dark:border-slate-700 dark:border-l-primary-light dark:bg-slate-800 motion-safe:hover:shadow-md sm:p-5 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 dark:focus-visible:ring-primary-light/40 dark:focus-visible:ring-offset-slate-900"
            aria-label={t("sponsoringBenefits.download_aria")}
          >
            <div
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary/10 transition-transform duration-300 dark:bg-primary-light/15 motion-safe:group-hover:scale-105 sm:h-14 sm:w-14"
              aria-hidden
            >
              <i className="fas fa-file-pdf text-xl text-primary dark:text-primary-light sm:text-2xl" />
            </div>
            <div className="min-w-0 flex-1 text-left">
              <span className="block text-base font-semibold text-slate-900 dark:text-slate-100 sm:text-lg">
                {t("sponsoringBenefits.downloadEventmappe")}
              </span>
              <span className="mt-0.5 block text-sm text-slate-500 dark:text-slate-400">
                {t("sponsoringBenefits.download_file_hint")}
              </span>
            </div>
            <span
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-600 transition-colors group-hover:bg-primary/10 group-hover:text-primary dark:bg-slate-700 dark:text-slate-300 dark:group-hover:bg-primary-light/15 dark:group-hover:text-primary-light"
              aria-hidden
            >
              <i className="fas fa-download text-sm" />
            </span>
          </a>
        </div>
      </div>
    </section>
  );
}

export default SponsoringBenefits;
